import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords if changing
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        showToast('New passwords do not match', 'error');
        setLoading(false);
        return;
      }
      if (formData.newPassword.length < 6) {
        showToast('Password must be at least 6 characters', 'error');
        setLoading(false);
        return;
      }
    }

    const updateData = {
      name: formData.name,
      email: formData.email
    };

    if (formData.newPassword) {
      updateData.password = formData.newPassword;
    }

    const result = await updateProfile(updateData);

    if (result.success) {
      showToast('Profile updated successfully!', 'success');
      setEditing(false);
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      showToast(result.error || 'Failed to update profile', 'error');
    }

    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show loading if user is not loaded yet
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-2 african-accent inline-block">
            My Profile
          </h1>
          <p className="text-gray-600">Manage your account settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              {/* Avatar */}
              <div className="w-32 h-32 mx-auto mb-4 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-african">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                {user?.name}
              </h2>
              
              <p className="text-orange-600 font-semibold mb-4 capitalize">
                {user?.role === 'artist' ? '🎨 Artist' : user?.role === 'admin' ? '👑 Admin' : '🛍️ Art Lover'}
              </p>

              <div className="border-t border-orange-100 pt-4 mt-4 space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong><br />
                  {user?.email}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Member Since:</strong><br />
                  {new Date(user?.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>

              <button
                onClick={handleLogout}
                className="w-full mt-6 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-display font-bold text-gray-900">
                  Account Information
                </h3>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="btn-secondary"
                  >
                    Edit Profile
                  </button>
                )}
              </div>

              {editing ? (
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                    />
                  </div>

                  <div className="border-t border-orange-100 pt-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      Change Password (Optional)
                    </h4>

                    {/* New Password */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                        placeholder="Leave blank to keep current password"
                      />
                    </div>

                    {/* Confirm Password */}
                    {formData.newPassword && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200"
                          placeholder="Confirm new password"
                        />
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 btn-primary disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          name: user?.name || '',
                          email: user?.email || '',
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      className="flex-1 btn-outline"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Full Name</p>
                    <p className="text-gray-900">{user?.name}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Email Address</p>
                    <p className="text-gray-900">{user?.email}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Account Type</p>
                    <p className="text-gray-900 capitalize">{user?.role}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
              <h3 className="text-xl font-display font-bold text-gray-900 mb-4">
                Quick Links
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/orders')}
                  className="p-4 border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 text-left"
                >
                  <p className="font-semibold text-gray-900">📦 My Orders</p>
                  <p className="text-sm text-gray-600 mt-1">View order history</p>
                </button>
                <button
                  onClick={() => navigate('/messages')}
                  className="p-4 border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 text-left"
                >
                  <p className="font-semibold text-gray-900">💬 Messages</p>
                  <p className="text-sm text-gray-600 mt-1">Chat with artists</p>
                </button>
                {user?.role === 'artist' && (
                  <button
                    onClick={() => navigate('/artist-dashboard')}
                    className="p-4 border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 text-left"
                  >
                    <p className="font-semibold text-gray-900">🎨 My Artworks</p>
                    <p className="text-sm text-gray-600 mt-1">Manage your art</p>
                  </button>
                )}
                {user?.role === 'admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="p-4 border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 text-left"
                  >
                    <p className="font-semibold text-gray-900">👑 Admin Panel</p>
                    <p className="text-sm text-gray-600 mt-1">Manage platform</p>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;